import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Download, Trophy, Flame, Target, MoreVertical } from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import {
  Button, Card, Spinner, Badge, SearchInput, PaginationBar,
} from '../../../components/ui'
import { Thead, Tbody, Tr, Th, Td } from '../../../components/ui/Table'

interface Member {
  id: number; user_id: number; name: string; phone: string; email: string | null
  rank: string; xp: number; total_points: number; missions_completed: number
  streak_days: number; joined_at: string; status?: string
}

const rankConfig: Record<string, { label: string; color: string; variant: string }> = {
  bronze: { label: 'Bronze', color: '#CD7F32', variant: 'warning' },
  prata: { label: 'Prata', color: '#A0AEC0', variant: 'gray' },
  ouro: { label: 'Ouro', color: '#F59E0B', variant: 'warning' },
}

export default function CommunityMembersPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [members, setMembers] = useState<Member[]>([])
  const [search, setSearch] = useState('')
  const [rankFilter, setRankFilter] = useState('')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(20)
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 20, last_page: 1 })

  const fetchMembers = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = { search, page, per_page: perPage }
      if (rankFilter) params.rank = rankFilter
      const res = await axios.get(buildApiUrl('/v1/community/members'), { params })
      setMembers(res.data.data)
      if (res.data.meta) setMeta(res.data.meta)
    } catch {} finally { setLoading(false) }
  }, [search, page, perPage, rankFilter])

  useEffect(() => { fetchMembers() }, [fetchMembers])
  useEffect(() => { setPage(1) }, [search, rankFilter, perPage])

  const formatDate = (d: string) => new Date(d).toLocaleDateString('pt-BR')
  const formatPhone = (p: string) => p ? `(${p.slice(0,2)}) ${p.slice(2,7)}-${p.slice(7)}` : '—'
  const [actionMenu, setActionMenu] = useState<number | null>(null)

  const statusConfig: Record<string, { label: string; color: string }> = {
    active: { label: 'Ativo', color: 'success' },
    suspended: { label: 'Suspenso', color: 'warning' },
    banned: { label: 'Banido', color: 'error' },
  }

  async function changeMemberStatus(memberId: number, status: string) {
    try {
      await axios.put(buildApiUrl(`/v1/community/members/${memberId}/status`), { status })
      toast('success', status === 'banned' ? 'Usuário banido' : status === 'suspended' ? 'Usuário suspenso' : 'Usuário reativado')
      fetchMembers()
    } catch { toast('error', 'Erro ao atualizar status') }
    setActionMenu(null)
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/community/dashboard')} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Membros</h1>
            <p className="text-gray-500 mt-1">{meta.total} membros na comunidade</p>
          </div>
        </div>
      </div>

      {/* Busca + Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <SearchInput placeholder="Buscar por nome ou telefone..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-1">
          {['', 'bronze', 'prata', 'ouro'].map(rank => (
            <button
              key={rank}
              onClick={() => setRankFilter(rank)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                rankFilter === rank ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {rank ? rankConfig[rank]?.label : 'Todos'}
            </button>
          ))}
        </div>
      </div>

      {/* Tabela */}
      <Card padding="none">
        <div className="overflow-x-auto">
          <table className="w-full">
            <Thead>
              <Tr>
                <Th>Membro</Th>
                <Th>Telefone</Th>
                <Th>Rank</Th>
                <Th>Pontos</Th>
                <Th>Missões</Th>
                <Th>Streak</Th>
                <Th>Status</Th>
                <Th>Entrou em</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <Tr key={i}>
                    {Array.from({ length: 9 }).map((_, j) => (
                      <Td key={j}><div className="h-4 bg-gray-100 rounded animate-pulse" style={{ width: `${50 + Math.random() * 50}%` }} /></Td>
                    ))}
                  </Tr>
                ))
              ) : members.length === 0 ? (
                <Tr><Td colSpan={9} className="text-center text-gray-500 py-8">Nenhum membro encontrado</Td></Tr>
              ) : members.map(m => {
                const rc = rankConfig[m.rank] || rankConfig.bronze
                return (
                  <Tr key={m.id}>
                    <Td>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-600 flex items-center justify-center text-xs font-bold">
                          {m.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">{m.name}</p>
                          {m.email && <p className="text-xs text-gray-400">{m.email}</p>}
                        </div>
                      </div>
                    </Td>
                    <Td><span className="text-sm text-gray-600">{formatPhone(m.phone)}</span></Td>
                    <Td>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: `${rc.color}20`, color: rc.color }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: rc.color }} />
                        {rc.label}
                      </span>
                    </Td>
                    <Td>
                      <span className="flex items-center gap-1 text-sm font-medium text-gray-800">
                        <Trophy className="w-3.5 h-3.5 text-warning-500" />
                        {m.total_points}
                      </span>
                    </Td>
                    <Td>
                      <span className="flex items-center gap-1 text-sm text-gray-600">
                        <Target className="w-3.5 h-3.5 text-brand-400" />
                        {m.missions_completed}
                      </span>
                    </Td>
                    <Td>
                      <span className="flex items-center gap-1 text-sm text-gray-600">
                        <Flame className="w-3.5 h-3.5 text-warning-400" />
                        {m.streak_days}d
                      </span>
                    </Td>
                    <Td>
                      {(() => {
                        const sc = statusConfig[m.status || 'active'] || statusConfig.active
                        return <Badge variant={sc.color as any}>{sc.label}</Badge>
                      })()}
                    </Td>
                    <Td><span className="text-sm text-gray-500">{formatDate(m.joined_at)}</span></Td>
                    <Td>
                      <div className="relative">
                        <button onClick={() => setActionMenu(actionMenu === m.id ? null : m.id)} className="p-1.5 rounded-lg hover:bg-gray-100">
                          <MoreVertical className="w-4 h-4 text-gray-400" />
                        </button>
                        {actionMenu === m.id && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setActionMenu(null)} />
                            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-20 py-1 min-w-[140px]">
                              {m.status !== 'active' && (
                                <button onClick={() => changeMemberStatus(m.id, 'active')} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">Reativar</button>
                              )}
                              {m.status !== 'suspended' && (
                                <button onClick={() => changeMemberStatus(m.id, 'suspended')} className="w-full text-left px-4 py-2 text-sm text-warning-600 hover:bg-gray-50">Suspender</button>
                              )}
                              {m.status !== 'banned' && (
                                <button onClick={() => changeMemberStatus(m.id, 'banned')} className="w-full text-left px-4 py-2 text-sm text-error-500 hover:bg-gray-50">Banir</button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </table>
        </div>
        <PaginationBar
          page={page}
          perPage={perPage}
          total={meta.total}
          lastPage={meta.last_page}
          onPageChange={setPage}
          onPerPageChange={n => { setPerPage(n); setPage(1) }}
        />
      </Card>
    </div>
  )
}
